import React from "react";
import {
  FiArrowUpRight,
  FiArrowUp,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
const { t } = useTranslation("footer");


  return (
    <footer className="overflow-hidden bg-[#E8EEF0] text-[#263238]">

      <div className="mx-auto max-w-[1440px] px-5 md:px-8">

        {/* =====================================================
            TOP LINE
        ====================================================== */}
        <div
          data-aos="fade-down"
          data-aos-delay="0"
          className="flex items-center justify-between border-b border-[#263238]/10 py-5"
        >

          <div
            data-aos="fade-right"
            data-aos-delay="40"
            className="flex items-center gap-3"
          >

            <span className="h-1.5 w-1.5 rounded-full bg-[#C8A96B]" />

            <span className="text-[9px] font-bold uppercase tracking-[0.24em] text-[#263238]/50">
              Crypsyn
            </span>

          </div>

          <span
            data-aos="fade-left"
            data-aos-delay="60"
            className="font-mono text-[9px] text-[#263238]/25"
          >
            {/* END / 001 */}
            {t("brand.endLabel")}
          </span>

        </div>

        {/* =====================================================
            MAIN FOOTER INTRO
        ====================================================== */}
        <div className="grid gap-12 border-b border-[#263238]/10 py-16 md:py-20 lg:grid-cols-[1.4fr_0.6fr]">

          {/* LEFT */}
          <div>

            <p
              data-aos="fade-right"
              data-aos-delay="0"
              className="max-w-xs text-[10px] font-bold uppercase tracking-[0.2em] text-[#263238]/40"
            >
               {t("intro.eyebrow")}
            </p>

            <h2
              data-aos="fade-up"
              data-aos-delay="50"
              className="mt-8 max-w-4xl text-[clamp(4rem,8vw,8rem)] font-semibold leading-[0.8] tracking-[-0.085em]"
            >
              {t("intro.titleLine1")}
              <span className="block text-[#8C9A9D]">
                {t("intro.titleLine2")}
              </span>
            </h2>

            <p
              data-aos="fade-right"
              data-aos-delay="100"
              className="mt-9 max-w-lg text-sm leading-7 text-[#263238]/45"
            >
              {t("intro.description")}
              {/* Questions, ideas, feedback or something you'd like
              to discuss? We're always open to a conversation. */}
            </p>

            <a
              href="/contact"
              data-aos="zoom-in"
              data-aos-delay="140"
              className="group mt-8 inline-flex items-center gap-3 border-b border-[#263238]/20 pb-2 text-sm font-semibold transition-colors hover:border-[#C8A96B]"
            >
              {/* Contact our team */}
              {t("intro.contactCta")}
              <FiArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>

          </div>

          {/* RIGHT INFORMATION */}
          <div
            data-aos="fade-left"
            data-aos-delay="80"
            className="flex flex-col justify-end"
          >

            <div className="border-l border-[#263238]/10 pl-7">

              <p
                data-aos="fade-down"
                data-aos-delay="120"
                className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
              >
                Operated by
              </p>

              <h3
                data-aos="fade-left"
                data-aos-delay="150"
                className="mt-3 text-2xl font-semibold tracking-[-0.04em]"
              >
                Mailfinance AG
              </h3>

              <p
                data-aos="fade-left"
                data-aos-delay="190"
                className="mt-4 max-w-xs text-sm leading-6 text-[#263238]/40"
              >
                Crypsyn is operated by Mailfinance AG,
                a company based in Switzerland.
              </p>

              <div
                data-aos="zoom-in"
                data-aos-delay="230"
                className="mt-6 flex items-center gap-3"
              >

                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D7E4E1]">
                  <FiMapPin size={13} />
                </span>

                <span className="text-xs font-medium text-[#263238]/55">
                  Switzerland
                </span>

              </div>

            </div>

          </div>

        </div>

        {/* =====================================================
            NAVIGATION
        ====================================================== */}
        <div className="grid border-b border-[#263238]/10 md:grid-cols-4">

          {/* EXPLORE */}
          <div
            data-aos="fade-right"
            data-aos-delay="0"
            className="border-b border-[#263238]/10 py-9 md:border-b-0 md:border-r md:pr-8"
          >

            <p
              data-aos="fade-down"
              data-aos-delay="40"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              {t("navigation.explore.title")}
            </p>

            <nav className="mt-6 flex flex-col gap-4">

              <a
                href="/"
                data-aos="fade-right"
                data-aos-delay="70"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Home */}
                {t("navigation.explore.hero")}
              </a>

              <a
                href="/about-us"
                data-aos="fade-right"
                data-aos-delay="100"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* About */}
                {t("navigation.explore.about")}
              </a>

              <a
                href="/contact"
                data-aos="fade-right"
                data-aos-delay="130"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Contact */}
                {t("navigation.explore.contact")}
              </a>

            </nav>

          </div>

          {/* RESOURCES */}
          <div
            data-aos="fade-up"
            data-aos-delay="50"
            className="border-b border-[#263238]/10 py-9 md:border-b-0 md:border-r md:px-8"
          >

            <p
              data-aos="fade-down"
              data-aos-delay="80"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              {/* Resources */}
              {t("navigation.resources.title")}
            </p>

            <nav className="mt-6 flex flex-col gap-4">

              <a
                href="/documentation"
                data-aos="fade-up"
                data-aos-delay="110"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Documentation */}
                {t("navigation.resources.documentation")}
              </a>

              <a
                href="/help-center"
                data-aos="fade-up"
                data-aos-delay="140"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {t("navigation.resources.helpCenter")}
                {/* Help Center */}
              </a>

              <a
                href="/faq"
                data-aos="fade-up"
                data-aos-delay="170"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* FAQ */}
                {t("navigation.resources.faq")}
              </a>

            </nav>

          </div>

          {/* COMPANY */}
          <div
            data-aos="fade-left"
            data-aos-delay="80"
            className="border-b border-[#263238]/10 py-9 md:border-b-0 md:border-r md:px-8"
          >

            <p
              data-aos="zoom-in"
              data-aos-delay="110"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              {/* Company */}
              {t("navigation.company.title")}
            </p>

            <nav className="mt-6 flex flex-col gap-4">

              <a
                href="/company"
                data-aos="fade-left"
                data-aos-delay="140"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Company */}
                 {t("navigation.company.company")}
              </a>

              <a
                href="/security"
                data-aos="fade-left"
                data-aos-delay="170"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Security */}
                 {t("navigation.company.security")}
              </a>

              <a
                href="/privacy-policy"
                data-aos="fade-left"
                data-aos-delay="200"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Privacy */}
                 {t("navigation.company.privacy")}
              </a>

              <a
                href="/terms-and-conditions"
                data-aos="fade-left"
                data-aos-delay="230"
                className="w-fit text-sm text-[#263238]/55 transition-colors hover:text-[#C8A96B]"
              >
                {/* Terms */}
                 {t("navigation.company.terms")}
              </a>

            </nav>

          </div>

          {/* CONTACT */}
          <div
            data-aos="fade-left"
            data-aos-delay="120"
            className="py-9 md:pl-8"
          >

            <p
              data-aos="fade-down"
              data-aos-delay="150"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              {/* Contact */}
               {t("navigation.contact.title")}
            </p>

            <div className="mt-6 space-y-5">

              <a
                href="mailto:hello@crypsyn.com"
                data-aos="zoom-in"
                data-aos-delay="180"
                className="group flex items-center gap-3"
              >

                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#D7E4E1]">
                  <FiMail size={13} />
                </span>

                <span className="text-sm text-[#263238]/55 transition-colors group-hover:text-[#C8A96B]">
                  hello@crypsyn.com
                </span>

              </a>

              <div
                data-aos="fade-left"
                data-aos-delay="210"
                className="flex items-center gap-3"
              >

                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#D7E4E1]">
                  <FiMapPin size={13} />
                </span>

                <span className="text-sm text-[#263238]/55">
                  Switzerland
                </span>

              </div>

              <a
                href="/contact"
                data-aos="fade-up"
                data-aos-delay="240"
                className="group inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.12em]"
              >
                {/* Get in touch */}
                {t("navigation.contact.getInTouch")}

                <FiArrowUpRight
                  size={12}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />

              </a>

            </div>

          </div>

        </div>

        {/* =====================================================
            COMPANY / LEGAL INFORMATION
        ====================================================== */}
        <div className="grid gap-8 border-b border-[#263238]/10 py-9 md:grid-cols-2">

          {/* Company details */}
          <div
            data-aos="fade-right"
            data-aos-delay="0"
          >

            <p
              data-aos="fade-down"
              data-aos-delay="40"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              {/* Corporate information */}
              {t("corporate.title")}
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">

              <div
                data-aos="fade-right"
                data-aos-delay="80"
              >
                <p className="text-[9px] uppercase tracking-[0.14em] text-[#263238]/25">
                  {/* Legal entity */}
                  {t("corporate.legalEntityLabel")}
                </p>

                <p
                  data-aos="fade-up"
                  data-aos-delay="110"
                  className="mt-1 text-xs font-semibold"
                >
                  {/* Mailfinance AG */}
                  {t("corporate.legalEntity")}  
                </p>
              </div>

              <div
                data-aos="fade-left"
                data-aos-delay="80"
              >
                <p className="text-[9px] uppercase tracking-[0.14em] text-[#263238]/25">
                  {/* Country */}
                  {t("corporate.countryLabel")}
                </p>

                <p
                  data-aos="fade-up"
                  data-aos-delay="110"
                  className="mt-1 text-xs font-semibold"
                >
                  Switzerland
                </p>
              </div>

            </div>

          </div>

          {/* Brand statement */}
          <div
            data-aos="fade-left"
            data-aos-delay="60"
            className="md:border-l md:border-[#263238]/10 md:pl-8"
          >

            <p
              data-aos="fade-down"
              data-aos-delay="100"
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#263238]/35"
            >
              Crypsyn
            </p>

            <p
              data-aos="fade-left"
              data-aos-delay="140"
              className="mt-4 max-w-md text-sm leading-6 text-[#263238]/40"
            >
              {/* Technology and communication infrastructure
              built with clarity, reliability and purpose. */}
              {t("brandStatement.description")}
            </p>

          </div>

        </div>

        {/* =====================================================
            BOTTOM BAR
        ====================================================== */}
        <div className="flex flex-col gap-5 py-7 md:flex-row md:items-center md:justify-between">

          <div
            data-aos="fade-right"
            data-aos-delay="0"
            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-5"
          >

            <span
              data-aos="fade-right"
              data-aos-delay="40"
              className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#263238]/30"
            >
              © {new Date().getFullYear()} Crypsyn
            </span>

            <span className="hidden h-3 w-px bg-[#263238]/10 sm:block" />

            <span
              data-aos="fade-left"
              data-aos-delay="80"
              className="text-[9px] uppercase tracking-[0.15em] text-[#263238]/25"
            >
              {/* Operated by Mailfinance AG */}
              {t("bottomBar.operatedBy")}
            </span>

          </div>

          <div
            data-aos="fade-left"
            data-aos-delay="60"
            className="flex items-center gap-6"
          >

            <a
              href="/privacy-policy"
              data-aos="fade-up"
              data-aos-delay="100"
              className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#263238]/30 transition-colors hover:text-[#C8A96B]"
            >
              {/* Privacy */}
              {t("bottombar.privacy")}
            </a>

            <a
              href="/terms-and-conditions"
              data-aos="fade-down"
              data-aos-delay="130"
              className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#263238]/30 transition-colors hover:text-[#C8A96B]"
            >
              {/* Terms */}
              {t("bottombar.terms")}
            </a>

            <button
              type="button"
              onClick={scrollToTop}
              data-aos="zoom-in"
              data-aos-delay="160"
              aria-label="Back to top"
              className="group flex h-9 w-9 items-center justify-center rounded-full border border-[#263238]/15 transition-all duration-300 hover:border-[#C8A96B] hover:bg-[#C8A96B]"
            >
              <FiArrowUp
                size={13}
                className="transition-transform duration-300 group-hover:-translate-y-0.5"
              />
            </button>

          </div>

        </div>

      </div>

      {/* =====================================================
          BOTTOM ACCENT
      ====================================================== */}
      <div
        data-aos="zoom-in"
        data-aos-duration="500"
        data-aos-delay="0"
        className="flex h-1"
      >

        <div className="w-2/3 bg-[#D7E4E1]" />

        <div className="w-1/3 bg-[#C8A96B]" />

      </div>

    </footer>
  );
};

export default Footer;